import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { queryKeys } from '@/constant/common'
import { QueryClient } from '@tanstack/react-query'

export interface SubInterface {
  interfaceId: number
  /** Position on the hub's two-wire, 1-based. */
  twiId?: number
  /** The controller's system name, e.g. `sgp-sg-cis-dev1-jl`. */
  name?: string
  /** What the site calls it, e.g. `Demo Controller D1`. Prefer this. */
  customName?: string
}

const SUBS_STALE_TIME = 5 * 60 * 1000

/**
 * Shared cache key for a controller's sub-controller list. `useIsControlHub`,
 * `useDecoderProtocolMap` and `useSearchAllIOConfig` all need it, so they read
 * it from one cache entry rather than each firing a separate `/subs` request.
 */
export const subsQueryKey = (interfaceId: number) =>
  [queryKeys.controller, 'subs', interfaceId] as const

/** Raw sub-controller list for an interface (empty for a plain controller). */
export const fetchSubInterfaces = async (
  interfaceId: number,
): Promise<SubInterface[]> => {
  const { data } = await controllerDataConfigApi.getSubInterfaces(interfaceId)
  return (data?.data ?? data ?? []) as SubInterface[]
}

/**
 * The interfaces that actually hold a controller's line units and IO configs.
 *
 * A control hub owns none of its own — they live on its sub-controllers, each a
 * separate interface. So resolve to the sub-controller interface ids when the
 * given interface is a hub, and to the interface itself otherwise (a plain
 * controller, or when the subs endpoint is unavailable).
 *
 * The subs lookup goes through the query client so the result is shared with
 * the other consumers (`useIsControlHub` and the sibling data-map hook) instead
 * of firing a fresh `/subs` request on every call.
 */
export const resolveSourceInterfaceIds = async (
  interfaceId: number,
  queryClient: QueryClient,
): Promise<number[]> => {
  try {
    const subs = await queryClient.fetchQuery({
      queryKey: subsQueryKey(interfaceId),
      queryFn: () => fetchSubInterfaces(interfaceId),
      staleTime: SUBS_STALE_TIME,
    })
    const subIds = subs
      .map((sub) => Number(sub.interfaceId))
      .filter((id) => id > 0)
    if (subIds.length) return subIds
  } catch {
    // Not a hub / endpoint unavailable — fall back to this interface.
  }
  return [interfaceId]
}
